import { request } from 'services/v3ApiClient';
import axios, { type AxiosResponse } from 'axios';
import { API_ORIGIN_EXTERNAL, sendVideoEvents } from 'actions/videos';

export const MULTIPART_UPLOAD_MAX_PART_SIZE = 1048576 * 50; // 50 MB

type Part = {
  part_number: number;
  url: string;
};

type SignedUrlData = {
  urls: Part[];
  expires_at: string;
  upload_id: string;
  filename: string;
};

type CreateMultipartUploadResponse = {
  data: SignedUrlData;
  errors: unknown[];
  meta: {
    has_next_page: boolean;
    has_previous_page: boolean;
    next_page: string;
    previous_page: string;
  };
};

const createMultipartUpload = async (file: File) => {
  const response: AxiosResponse<CreateMultipartUploadResponse> = await request({
    method: 'post',
    url: `/multipart_uploads`,
    params: {
      filename: file.name,
      mime_type: file.type,
      part_size: MULTIPART_UPLOAD_MAX_PART_SIZE,
      file_size: file.size,
    },
  });

  return response.data;
};

const uploadParts = async ({ file, urls }: { file: File; urls: Part[] }) => {
  const client = axios.create();
  const config = {
    headers: { 'Content-Type': 'application/octet-stream' },
  };

  const uploadPromises = urls.map(async (part: Part) => {
    const { url, part_number } = part;
    const start = (part_number - 1) * MULTIPART_UPLOAD_MAX_PART_SIZE;
    const end = part_number * MULTIPART_UPLOAD_MAX_PART_SIZE;
    const blobPart =
      part_number === urls.length ? file.slice(start) : file.slice(start, end);

    const response = await client.put(url, blobPart, config);

    try {
      const etag: string = response.headers['etag'].replace(/['"]/g, '');
      return {
        part_number,
        etag,
      };
    } catch (e) {
      throw new Error(
        `multipartUpload web - Etag header is missing.  Error: ${e}`,
      );
    }
  });

  const partData = await Promise.all(uploadPromises).catch((error) => {
    throw error;
  });
  return partData;
};

type CompleteMultipartUploadResponse = {
  data: {
    node_id: string;
    id: number;
    origin: 'external' | 'internal';
  };
};

const completeMultipartUpload = async ({
  projectId,
  filename,
  mimeType,
  upload_id,
  duration,
  partData,
}: {
  projectId: number;
  filename: string;
  mimeType: string;
  upload_id: string;
  duration: number;
  partData: {
    part_number: number;
    etag: string;
  }[];
}) => {
  const response: AxiosResponse<CompleteMultipartUploadResponse> =
    await request({
      method: 'post',
      url: `/projects/${projectId}/videos/complete_multipart_uploads`,
      data: {
        filename,
        mime_type: mimeType,
        upload_id,
        part_data: partData,
        video: {
          capture_date: new Date().toISOString(),
          duration,
          origin: API_ORIGIN_EXTERNAL,
        },
      },
    });
  return response.data;
};

export const beginMultipartUpload = async ({
  file,
  duration,
  projectId,
  userId,
}: {
  file: File;
  duration: number;
  projectId: number;
  userId: number;
}) => {
  // 1. Start multipart upload - get signed urls
  const createResponse = await createMultipartUpload(file);
  const { urls, filename, upload_id } = createResponse.data;

  // 2. Upload parts to S3
  const partData = await uploadParts({ file, urls });

  // 3. Complete multipart upload
  const completeResponse = await completeMultipartUpload({
    projectId,
    filename,
    mimeType: file.type,
    upload_id,
    duration,
    partData,
  });
  const assetId = completeResponse.data.id;

  sendVideoEvents({
    assetId,
    userId,
    projectId,
    duration,
    uploadType: 'multipart',
  });
};
